(ns ui.cli.page
  "The Storrito CLI page: install the command-line tool, sign in, create
   a first draft and connect a coding agent. The content lives in
   `ui.cli.guide`, this namespace is the layout."
  (:require [storrito.ui.core :as ui]
            [ui.app-frame :as app-frame]
            [ui.cli.guide :as guide]
            [ui.elements :as elements]))

(def copy-button-class
  ["inline-flex" "items-center" "shrink-0" "px-3" "py-1.5" "text-xs"
   "font-medium" "text-gray-700" "bg-gray-100" "rounded" "hover:bg-gray-200"
   "cursor-pointer" "whitespace-nowrap"])

(defn copy-button
  "Copies `text` to the clipboard and shows `Copied!` for a moment, see
   the `:clipboard/copy` action in `ui.action-handlers`."
  [state text store-key]
  [:button {:class copy-button-class
            :on {:click [[:clipboard/copy text store-key]]}}
   (if (get state store-key) "Copied!" "Copy")])

(defn command
  "One command line with a Copy button. `store-key` must be unique per
   command on the page."
  [state {:keys [label text]} store-key]
  [:div
   (when label
     [:p {:class ["mb-1" "text-xs" "font-medium" "text-gray-500"]} label])
   [:div {:class ["flex" "items-center" "gap-3" "rounded-lg" "border"
                  "border-gray-200" "bg-gray-50" "px-3" "py-2"]}
    [:code {:class ["min-w-0" "flex-1" "break-all" "text-sm" "text-gray-800"]}
     text]
    (copy-button state text store-key)]])

(defn command-list
  [state items key-prefix]
  (into [:div {:class ["space-y-2" "max-w-3xl"]}]
        (map-indexed (fn [i item]
                       (command state item (keyword "cli-page" (str key-prefix "-" i))))
                     items)))

(defn file-card
  "A file the user saves before the commands of a step."
  [state {:keys [name text]} store-key]
  [:div {:class ["max-w-3xl"]}
   [:div {:class ["flex" "items-center" "justify-between" "rounded-t-lg"
                  "border" "border-b-0" "border-gray-200" "bg-gray-100"
                  "px-3" "py-1.5"]}
    [:span {:class ["text-xs" "font-medium" "text-gray-600"]} name]
    (copy-button state text store-key)]
   [:pre {:class ["overflow-x-auto" "rounded-b-lg" "border" "border-gray-200"
                  "bg-gray-900" "p-3" "text-xs" "text-gray-100"]}
    text]])

(defn section
  [title & children]
  (into [:section {:class ["mt-12"]}
         [:h2 {:class (into ["mb-4"] elements/h2)} title]]
        children))

(defn step
  [state i {:keys [title description file commands]}]
  [:div {:class ["mt-8"]}
   [:h3 {:class (into ["flex" "items-center" "gap-3"] elements/h3)}
    [:span {:class ["inline-flex" "h-8" "w-8" "shrink-0" "items-center"
                    "justify-center" "rounded-full" "bg-gray-900" "text-sm"
                    "font-bold" "text-white"]}
     (str (inc i))]
    title]
   (when description
     [:p {:class (into ["mt-2" "max-w-3xl"] elements/p)} description])
   (when file
     [:div {:class ["mt-3"]}
      (file-card state file (keyword "cli-page" (str "file-" i)))])
   [:div {:class ["mt-3"]}
    (command-list state commands (str "step-" i))]])

(defn bullet-list
  [items]
  (into [:ul {:class ["list-disc" "space-y-2" "pl-5" "max-w-3xl"]}]
        (map (fn [item]
               [:li {:class elements/p} item])
             items)))

(defn panel
  "The page content. `state` carries the copied flags of the copy
   buttons, `org-uuid` personalizes the unattended example."
  [state {:keys [org-uuid]}]
  [:div {:class ["pb-12"]}
   [:h1 {:class ui/h1} "Storrito CLI"]
   [:p {:class ["mt-2" "max-w-3xl" "text-gray-700"]} guide/intro]
   [:p {:class (into ["mt-2" "max-w-3xl"] elements/p-sm)}
    "Open source under MIT: "
    [:a {:href guide/repo-url :target "_blank" :class ui/a-indigo} "github.com/storrito/storrito-cli"]
    ". Full documentation: "
    [:a {:href guide/docs-url :target "_blank" :class ui/a-indigo} "storrito.com/documentation/cli"]
    "."]
   (apply section "From install to a first draft"
          (map-indexed (partial step state) (guide/steps)))
   (section "For coding agents"
            (bullet-list guide/agent-notes))
   (section "Without a browser: CI and agents in the cloud"
            [:p {:class (into ["mb-3" "max-w-3xl"] elements/p)} guide/unattended-intro]
            (command-list state (guide/unattended-commands org-uuid) "unattended")
            [:p {:class (into ["mt-3"] elements/p-sm)}
             [:a {:href "/ui/api-credential/manage/" :class ui/a-indigo}
              "Manage API Credentials"]])
   (section "Staying current"
            [:p {:class (into ["max-w-3xl"] elements/p)} guide/upgrade-note]
            [:div {:class ["mt-3"]}
             (command-list state [{:text "storrito upgrade"}] "upgrade")])])

(defn render
  [state]
  (app-frame/app-frame
    state
    (panel state {:org-uuid (guide/org-uuid js/window.location.origin)})))

(def page
  {:page-id :pages/cli
   :route [["ui" "cli"]]
   :render #'render})
